/**
 * Phase2MachineBar.tsx
 * FEAT-2026-0007 Phase 6C.3 — Machine-bar sub-component extracted from Phase2WeeklyCell.
 *
 * Contains:
 *   - addDays:           local date arithmetic helper
 *   - useMachineBarDrag: hook tracking right-edge drag, persisting booking resize
 *   - MachineBar:        the slate-blue pill that shows machine info + drag handle
 *
 * Extracted to keep Phase2WeeklyCell.tsx under the 600-line cap (6C.6 additions).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { alpha, useTheme } from '@mui/material/styles';
import { BookingWeekStrip } from './BookingWeekStrip';

import { updateBookingPlacement } from '../../../../services/scheduleBookings';
import { fmtShortDate } from '../../../../utils/weekUtils';
import { bookingLabelRange, isContinuousRun, CONTINUOUS_HINT } from '../../../../utils/runModeRendering';

// ---------------------------------------------------------------------------
// Date helper
// ---------------------------------------------------------------------------

/**
 * Add n calendar days to an ISO date string.
 */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Machine-bar drag-extend hook (6C.3)
// ---------------------------------------------------------------------------

interface MachineBarDragState {
  /** Dragging is in progress. */
  active: boolean;
  /** Px offset dragged since mousedown. */
  deltaX: number;
  /** Whether the async save is running. */
  saving: boolean;
  /** Error from the last save attempt, if any. */
  error: string | null;
}

interface UseMachineBarDragResult {
  dragState: MachineBarDragState;
  handleDragMouseDown: (e: React.MouseEvent) => void;
}

/**
 * Hook that tracks right-edge drag for a single machine bar.
 *
 * On mouseup:
 *  1. Calls updateBookingPlacement(bookingKey, { EndDate: newEndDate }) — the backend
 *     (resizeMachineBooking) now re-spans the linked employee staffing atomically, so
 *     this client no longer chains a second span call (which would double-respan).
 *  2. Calls onResized() so the parent reloads the grid (and the bus refreshes peers).
 *
 * `assignmentKeys` is retained (the booking's bound assignment keys) for callers /
 * future use; the re-span itself is owned by the backend.
 *
 * The "pixels per day" estimate is 180px ÷ 7 days ≈ 25.7 px/day (column width).
 */
export function useMachineBarDrag(
  bookingKey: string,
  bookingStart: string,
  bookingEnd: string,
  assignmentKeys: string[],
  onResized: () => void,
): UseMachineBarDragResult {
  const [dragState, setDragState] = useState<MachineBarDragState>({
    active: false,
    deltaX: 0,
    saving: false,
    error: null,
  });

  const startXRef = useRef(0);
  const currentEndRef = useRef(bookingEnd);

  // Update ref when prop changes (new booking loaded).
  useEffect(() => {
    currentEndRef.current = bookingEnd;
  }, [bookingEnd]);

  const PX_PER_DAY = 180 / 7; // column width ÷ week days

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const delta = e.clientX - startXRef.current;
    setDragState((prev) => ({ ...prev, deltaX: delta }));
  }, []);

  const handleMouseUp = useCallback(
    async (e: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp as EventListener);

      const delta = e.clientX - startXRef.current;
      const extraDays = Math.round(delta / PX_PER_DAY);

      setDragState((prev) => ({ ...prev, active: false, deltaX: 0 }));

      if (extraDays === 0) return; // no change

      const newEndDate = addDays(currentEndRef.current, extraDays);
      // Guard: cannot move end before start.
      if (newEndDate < bookingStart) return;

      setDragState((prev) => ({ ...prev, saving: true, error: null }));
      try {
        // Backend re-spans the linked staffing inside this same booking update, so
        // no client-side re-fan happens here (avoids double-respan; the old
        // spanAssignmentsForBookingResize helper was deleted as dead code, P-17a).
        await updateBookingPlacement(bookingKey, { EndDate: newEndDate });
        currentEndRef.current = newEndDate;
        setDragState((prev) => ({ ...prev, saving: false }));
        onResized();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Resize failed';
        setDragState((prev) => ({ ...prev, saving: false, error: msg }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingKey, bookingStart, assignmentKeys, onResized, handleMouseMove],
  );

  const handleDragMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      startXRef.current = e.clientX;
      setDragState({ active: true, deltaX: 0, saving: false, error: null });
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp as EventListener);
    },
    [handleMouseMove, handleMouseUp],
  );

  // Cleanup listeners on unmount.
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp as EventListener);
    };
  }, [handleMouseMove, handleMouseUp]);

  return { dragState, handleDragMouseDown };
}

// ---------------------------------------------------------------------------
// MachineBar sub-component
// ---------------------------------------------------------------------------

export interface MachineBarProps {
  bookingKey: string;
  bookingStart: string;
  bookingEnd: string;
  displayCode: string;
  displayName: string | null;
  effectiveStart: string;
  effectiveEnd: string;
  days: number;
  /**
   * Booking status — drives the "adding now vs already there" styling (Gigi, 2026-06-21):
   *   'Tentative'  → being planned in THIS scenario: hatched fill + "DRAFT" chip.
   *   'Committed'  → already booked on the live schedule: solid fill + "BOOKED" chip.
   */
  status: 'Tentative' | 'Committed';
  /** All assignment keys for this task (used by the re-fan after resize). */
  assignmentKeys: string[];
  onResized: () => void;
  /**
   * Hide the right-edge resize handle (Board view) — the bar reads as a plain label,
   * no misleading "draggable" affordance. Booking-resize stays in Classic + Phase 1.
   */
  hideResize?: boolean;
  /**
   * Hide the DRAFT/BOOKED status chip (Board view) — on a draft scenario the chip is
   * noise. The dashed-vs-solid left border still signals tentative vs committed.
   */
  hideStatus?: boolean;
  /**
   * Optional control rendered in the header's right slot (where the status chip sits) —
   * the Board passes its "+ add employee" button here so it lives inside the bar.
   */
  headerAction?: React.ReactNode;
  /**
   * ISO stat-holiday dates — forwarded to the mini week strip so a covered weekend / stat
   * holiday (e.g. Canada Day) greys out instead of lighting up as a worked day.
   */
  holidays?: string[];
  /**
   * The WORK label (work-order / project / quote) this booking belongs to — when set, the
   * bar LEADS with it (like the Machine Schedule) and drops the machine name to a small
   * sub-line. Null/undefined ⇒ the machine name stays the primary label (manual tasks).
   */
  workLabel?: string | null;
  /**
   * Run Mode Phase C — the task's RunMode snapshot. Continuous (24/7) bookings
   * keep their TRUE calendar span in the date label (no snap to working days —
   * a Saturday end is real), count days as calendar days tagged "(24/7)", and
   * render covered weekend cells in the week strip as running. Omit = attended.
   */
  runMode?: 'attended' | 'continuous' | null;
}

export const MachineBar: React.FC<MachineBarProps> = ({
  bookingKey,
  bookingStart,
  bookingEnd,
  displayCode,
  displayName,
  effectiveStart,
  effectiveEnd,
  // `days` (calendar) is intentionally not destructured — the label now derives a
  // working-day count via countWorkingDays. Kept on the props type for callers.
  status,
  assignmentKeys,
  onResized,
  hideResize = false,
  hideStatus = false,
  headerAction,
  holidays,
  workLabel,
  runMode,
}) => {
  const theme = useTheme();

  const { dragState, handleDragMouseDown } = useMachineBarDrag(
    bookingKey,
    bookingStart,
    bookingEnd,
    assignmentKeys,
    onResized,
  );

  const fullName = displayName ?? displayCode;
  // Lead with the WORK (WO / project / quote) like the Machine Schedule when we have it;
  // the machine name drops to a small sub-line. Manual tasks (no work label) keep the
  // machine name as the primary label.
  const primaryText = workLabel ?? fullName;
  const machineSubText = workLabel ? fullName : null;
  // The booking's per-week span is a raw calendar clamp [weekSat, weekFri], so when a test
  // spills across a week boundary the label would read the Saturday (e.g. "Aug 8 – Aug 11"
  // for a span whose real working days are Mon–Tue). Clamp the LABEL inward to the first /
  // last WORKING day (weekend + stat-holiday aware) so the dates match the carved schedule
  // (Gigi, 2026-06-25). The week strip keeps the raw span (it greys the weekend cells).
  // Run Mode Phase C: CONTINUOUS bookings opt OUT of the snap — their weekend
  // boundary dates are real 24/7 run-time, so the raw span + a calendar-day
  // count tagged "(24/7)" is the honest label (bookingLabelRange branches).
  const continuous = isContinuousRun(runMode);
  const { labelStart, labelEnd, daysLabel } = bookingLabelRange(
    effectiveStart, effectiveEnd, holidays, runMode,
  );
  // Collapse a single-day booking to one date (no "Aug 11–Aug 11").
  const detailLine = labelStart === labelEnd
    ? `${daysLabel} · ${fmtShortDate(labelStart)}`
    : `${daysLabel} · ${fmtShortDate(labelStart)}–${fmtShortDate(labelEnd)}`;
  // Header label = just the date(s), pinned to the top-right of the title row (mockup
  // parity, Gigi 2026-06-24). The week strip below already shows the span visually, so
  // the "Nd ·" duration lives in the tooltip (detailLine) rather than eating a row.
  const dateLabel = labelStart === labelEnd
    ? fmtShortDate(labelStart)
    : `${fmtShortDate(labelStart)} – ${fmtShortDate(labelEnd)}`;

  // "Adding now vs already there": tentative bookings (this scenario's plan) carry a
  // DRAFT chip + a dashed left edge; committed bookings (already on the live
  // schedule) carry a BOOKED chip + lock + a solid left edge. NO hatch — the hatch
  // made the duration/dates hard to read (Gigi, 2026-06-22).
  const tentative = status === 'Tentative';
  const chipColor = tentative ? theme.schedule.machineTime : theme.schedule.committed;
  const statusWord = tentative ? 'Draft (this plan)' : 'Booked (live schedule)';

  return (
    <Tooltip
      title={`${workLabel ? `${workLabel} · ` : ''}${fullName} · ${detailLine} · ${statusWord}${continuous ? ` · ${CONTINUOUS_HINT}` : ''}`}
      placement="top"
      enterDelay={400}
    >
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.5,
        borderRadius: '4px',
        // Clean, flat fill (no hatch) so the dates read clearly. Tentative is a
        // touch lighter than committed; the chip carries the real distinction.
        backgroundColor: alpha(
          theme.schedule.machineTime,
          dragState.active ? 0.25 : tentative ? 0.1 : 0.18,
        ),
        borderLeft: `3px ${tentative ? 'dashed' : 'solid'} ${theme.schedule.machineTime}`,
        outline: dragState.active
          ? `2px dashed ${theme.schedule.machineTime}`
          : undefined,
        userSelect: 'none',
      }}
    >
      <PrecisionManufacturingIcon
        sx={{ fontSize: 13, color: theme.schedule.machineTime, flexShrink: 0 }}
      />
      {/* Title + date share ONE row (date pinned top-right, mockup parity) so the week
          strip gets its own line below instead of the date eating a third row. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 0.75 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{ fontSize: '0.74rem', color: theme.schedule.machineTime, fontWeight: 700, minWidth: 0 }}
          >
            {primaryText}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0, minWidth: 0 }}>
            {/* 24/7 mini-chip — continuous runs (Run Mode Phase C). */}
            {continuous && (
              <Typography
                component="span"
                sx={{
                  fontSize: '0.56rem',
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  lineHeight: 1.4,
                  px: 0.4,
                  borderRadius: '3px',
                  backgroundColor: theme.schedule.continuous,
                  color: theme.palette.getContrastText(theme.schedule.continuous),
                }}
              >
                24/7
              </Typography>
            )}
            <Typography
              noWrap
              sx={{ fontSize: '0.78rem', color: theme.schedule.machineTime, fontWeight: 800, letterSpacing: '0.01em', flexShrink: 0 }}
            >
              {dateLabel}
            </Typography>
          </Box>
        </Box>
        {/* Machine name as a small sub-line when the bar leads with the work label. */}
        {machineSubText && (
          <Typography
            variant="caption"
            noWrap
            sx={{ fontSize: '0.64rem', fontWeight: 600, color: alpha(theme.schedule.machineTime, 0.72) }}
          >
            {machineSubText}
          </Typography>
        )}
        <Box sx={{ mt: 0.25 }}>
          <BookingWeekStrip rangeStart={effectiveStart} rangeEnd={effectiveEnd} holidays={holidays} runMode={runMode} />
        </Box>
      </Box>

      {/* Status chip — "DRAFT" (adding now) vs "BOOKED" (already there). Hidden in the
          Board view (hideStatus): the dashed-vs-solid left border still signals it. */}
      {!hideStatus && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.1,
            borderRadius: '3px',
            flexShrink: 0,
            backgroundColor: alpha(chipColor, 0.16),
          }}
          aria-label={statusWord}
        >
          {tentative ? (
            <PendingOutlinedIcon sx={{ fontSize: 10, color: chipColor }} />
          ) : (
            <LockOutlinedIcon sx={{ fontSize: 10, color: chipColor }} />
          )}
          <Typography
            variant="caption"
            sx={{ fontSize: '0.52rem', fontWeight: 700, letterSpacing: '0.04em', color: chipColor }}
          >
            {tentative ? 'DRAFT' : 'BOOKED'}
          </Typography>
        </Box>
      )}

      {/* Optional header action (Board's "+ add" lives here, where BOOKED was). */}
      {headerAction}

      {/* Right-edge drag handle (6C.3) — hidden in the Board view (hideResize). */}
      {!hideResize && (dragState.saving ? (
        <CircularProgress size={10} sx={{ color: theme.schedule.machineTime }} />
      ) : (
        <Tooltip title="Drag to extend booking end date">
          <Box
            onMouseDown={handleDragMouseDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'ew-resize',
              color: theme.schedule.machineTime,
              opacity: 0.6,
              '&:hover': { opacity: 1 },
              flexShrink: 0,
            }}
          >
            <DragHandleIcon sx={{ fontSize: 12, transform: 'rotate(90deg)' }} />
          </Box>
        </Tooltip>
      ))}
      {!hideResize && dragState.error && (
        <Tooltip title={dragState.error}>
          <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.6rem' }}>
            !
          </Typography>
        </Tooltip>
      )}
    </Box>
    </Tooltip>
  );
};
